import React, { useState, Suspense } from 'react'
import { Box, Container, Typography, Link } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { usePageContentWidth } from '@/contexts/PageContentWidthContext'
import { useAuthStatus } from '@/contexts/AuthContext'
import { useUserRoleContext } from '@/contexts/UserRoleContext'

// Lazy so the feedback feature stays out of the public-route bundle (TRA-115);
// it only loads the first time a visitor opens the feedback modal.
const ShareFeedbackModal = React.lazy(() =>
    import('@/features/feedback/components/ShareFeedbackModal').then((m) => ({
        default: m.ShareFeedbackModal,
    }))
)

const Footer: React.FC = () => {
    const { t } = useTranslation()
    const { pageContentWidth } = usePageContentWidth()
    const { isAuthenticated } = useAuthStatus()
    const { role } = useUserRoleContext()
    const [feedbackModalOpen, setFeedbackModalOpen] = useState(false)
    // Stays true after the first open so closing doesn't unmount mid-animation
    const [feedbackModalMounted, setFeedbackModalMounted] = useState(false)

    const openFeedbackModal = () => {
        setFeedbackModalMounted(true)
        setFeedbackModalOpen(true)
    }

    const showPrivacyDataLink = isAuthenticated && role === 'user'

    // Use context value if set, otherwise fallback to 'md'
    const maxWidth = pageContentWidth ?? 'md'

    return (
        <Box
            component="footer"
            sx={{
                mt: 'auto',
                py: 3,
            }}
        >
            <Container maxWidth={maxWidth}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'center', sm: 'flex-start' },
                        gap: 2,
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                        }}
                    >
                        {t('common:copyright')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Link
                            component="button"
                            variant="body2"
                            onClick={openFeedbackModal}
                            sx={{
                                color: 'text.secondary',
                                textDecoration: 'none',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontFamily: 'inherit',

                                '&:hover': {
                                    textDecoration: 'underline',
                                },

                                '&:focus': {
                                    outline: '2px solid',
                                    outlineColor: 'primary.main',
                                    outlineOffset: '2px',
                                },
                            }}
                        >
                            {t('common:shareFeedback')}
                        </Link>
                        <Link
                            component={RouterLink}
                            to="/privacy"
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                textDecoration: 'none',

                                '&:hover': {
                                    textDecoration: 'underline',
                                },

                                '&:focus': {
                                    outline: '2px solid',
                                    outlineColor: 'primary.main',
                                    outlineOffset: '2px',
                                },
                            }}
                        >
                            {t('privacy:legal.privacy')}
                        </Link>
                        {showPrivacyDataLink && (
                            <Link
                                component={RouterLink}
                                to="/profile"
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    textDecoration: 'none',

                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },

                                    '&:focus': {
                                        outline: '2px solid',
                                        outlineColor: 'primary.main',
                                        outlineOffset: '2px',
                                    },
                                }}
                            >
                                {t('privacy:dataHub.title')}
                            </Link>
                        )}
                    </Box>
                </Box>
            </Container>
            {feedbackModalMounted && (
                <Suspense fallback={null}>
                    <ShareFeedbackModal
                        open={feedbackModalOpen}
                        onClose={() => setFeedbackModalOpen(false)}
                    />
                </Suspense>
            )}
        </Box>
    )
}

export default Footer
